/* Copyright (c) 2020-present CloudIO Inc */

import { Platform } from '../../platform/Platform';
import { ActionManager, ActionManagerFn } from '../../registry/Component.type';
import { baseActionManager, baseActionProps } from '../../registry/Components';
import { defaultValue } from './ActionDownloadChartData';

const ActionDownloadChartDataManager: ActionManagerFn<
  'ActionDownloadChartData'
> = (platform: Platform): ActionManager<'ActionDownloadChartData'> => {
  return {
    ...baseActionManager(platform, 'ActionDownloadChartData'),
    defaultValue,
    props: {
      ...baseActionProps,
      chartItemId: {
        label: 'Chart Item ID',
        type: 'ItemID',
        initialFilter: 'Chartjs',
        validateItemId: (value, { pageId, appUid, itemId }) => {
          const item = platform.getItem(appUid, pageId, value);
          if (item?.type !== 'Chartjs' && item?.type !== 'ChartJSTreemap') {
            return `Item id '${value}' doesn't belong to a Chart`;
          }
          return '';
        },
      },
      fileNameExpression: {
        label: 'File Name',
        type: 'expression',
        returnType: async () => 'string',
      },
    },
    replaceableWith: ['ActionDownloadChartImage'],
    name: 'Download Chart Data',
    getDisplayText: (item) => item.props.chartItemId,
    icon: { iconName: 'download' },
    description: `**Overview**<br/>
The Download Chart Data action is a reporting utility designed to export the underlying dataset of a specific chart component. Instead of capturing the visual representation, it extracts the raw data and provides it to the user as a CSV file.<br/><br/>
This component requires the target chart to be currently rendered and visible on the page to function correctly. It supports standard charts as well as Treemap variations, ensuring broad compatibility with the visualization library. It is designed to be easily swappable with the "Download Chart Image" action for users who need a visual snapshot instead of raw data.

**Key Features**<br/>
* **Raw Data Export:** Specifically configured to download the chart's data as a CSV, enabling further analysis in spreadsheet software.
* **Component Interchangeability:** Defined in the replaceableWith array, allowing developers to switch between downloading data (ActionDownloadChartData) and downloading the visual chart image (ActionDownloadChartImage).
* **Strict Type Validation:** Implements validateItemId to ensure the target ID belongs to a valid visualization component (Chartjs or ChartJSTreemap), preventing errors from targeting incompatible items.
* **Custom Filenaming:** Includes a fileNameExpression property, allowing builders to dynamically generate the filename (e.g., "Sales_Report_2023.csv") at runtime.

**Properties**<br/>
* **Chart Item ID (chartItemId):** An ItemID property identifying the chart to export.
  * *Validation:* Confirms the item is either a Chartjs or ChartJSTreemap component.
  * *Constraint:* The target chart must be rendered and visible.
* **File Name (fileNameExpression):** An expression property returning a string.
  * *Function:* Defines the name of the downloaded CSV file.

**Use Cases**<br/>
* **Self-Service Analytics:** Adding an "Export to Excel" button next to a revenue bar chart so users can download the numbers for their own reports.
* **Data Verification:** Enabling an analyst to download the raw numbers behind a trend line to verify the calculations.`,
  };
};

export default ActionDownloadChartDataManager;
